import React, { useState } from 'react';
import { validatePNR } from '../utils/helpers';
import bookingService from '../services/bookingService';

const TTRDashboard = () => {
  const [pnrInput, setPnrInput] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchAttempted, setSearchAttempted] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setBooking(null);
    setSearchAttempted(false);

    if (!pnrInput.trim()) {
      setError('Please enter a PNR');
      return;
    }

    if (!validatePNR(pnrInput)) {
      setError('Invalid PNR format');
      return;
    }

    setLoading(true);
    setSearchAttempted(true);

    try {
      const bookingData = await bookingService.getBookingByPnr(pnrInput.toUpperCase());
      if (bookingData) {
        setBooking(bookingData);
      } else {
        setError('Booking not found');
      }
    } catch (err) {
      setError('Error fetching booking: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkBoarded = async (passengerId) => {
    try {
      const updatedPassengers = booking.passengers.map(p => ({
        ...p,
        status: p.id === passengerId ? 'boarded' : p.status,
      }));

      await bookingService.markPassengersBoarded(booking.pnr, [passengerId]);
      setBooking({
        ...booking,
        passengers: updatedPassengers,
      });
      alert('Passenger marked as boarded');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">TTR Dashboard - Ticket Verification</h1>

        {/* Search Form */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <form onSubmit={handleSearch}>
            <div className="flex gap-4">
              <input
                type="text"
                value={pnrInput}
                onChange={(e) => setPnrInput(e.target.value.toUpperCase())}
                placeholder="Enter PNR (e.g., ABC1234DEF)"
                maxLength="10"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 font-semibold transition disabled:opacity-50"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Booking Details */}
        {booking && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-4">Booking Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-gray-600 text-sm">PNR</p>
                  <p className="font-bold text-lg">{booking.pnr}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Train ID</p>
                  <p className="font-semibold">{booking.trainId}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Status</p>
                  <p className="font-semibold text-green-600">{booking.status.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Passengers</p>
                  <p className="font-semibold">{booking.passengers.length}</p>
                </div>
              </div>
            </div>

            {/* Passenger List */}
            <h3 className="text-xl font-bold mb-4">Passengers</h3>
            <div className="space-y-3">
              {booking.passengers.map((passenger, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <p className="font-semibold">{passenger.name}</p>
                      <p className="text-sm text-gray-600">{passenger.category}</p>
                    </div>
                    <span className={`px-4 py-2 rounded font-semibold text-white ${
                      passenger.status === 'boarded' ? 'bg-green-500' :
                      passenger.status === 'no-show' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }`}>
                      {passenger.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                  {passenger.status !== 'boarded' && (
                    <button
                      onClick={() => handleMarkBoarded(passenger.id)}
                      className="w-full mt-2 bg-primary text-white py-2 rounded hover:bg-blue-700 transition"
                    >
                      Mark as Boarded
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Seat Details */}
            <h3 className="text-xl font-bold mt-6 mb-4">Seat Details</h3>
            <div className="flex flex-wrap gap-2">
              {booking.seats.map((seat, idx) => (
                <span key={idx} className="bg-primary text-white px-4 py-2 rounded">
                  Coach {seat.coachNumber} - Seat {seat.seatNumber}
                </span>
              ))}
            </div>
          </div>
        )}

        {!booking && searchAttempted && !error && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-lg text-gray-600">No booking found for this PNR</p>
          </div>
        )}

        {!searchAttempted && !booking && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-lg text-gray-600">Enter a PNR to verify ticket</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TTRDashboard;
